import classNames from "classnames";

import { type DeviceTypeRecord } from "@/shared/domain/deviceTypes";
import { useUpdateDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { Callout, TextInput } from "@/shared/foundation/primitives";
import { useEditableField } from "@/shared/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./DeviceType.module.css";

/**
 * The type's name as a click-to-edit heading, like a session's. Enter or blur
 * saves, Escape cancels. A name is required, so emptying the field cancels
 * the edit instead of saving.
 */
export function EditableDeviceTypeName({
  deviceType,
}: {
  deviceType: DeviceTypeRecord;
}) {
  const { mutateAsync: updateDeviceType, error } = useUpdateDeviceType();
  const { editable, currentValue, beginEdit, cancel, save } = useEditableField({
    initial: deviceType.name,
    onSave: (trimmed) =>
      updateDeviceType({
        deviceTypeId: deviceType.device_type_id,
        resourceOwnerId: deviceType.org_id,
        updates: { name: trimmed },
      }),
  });

  const commit = (value: string) => {
    if (value.trim()) {
      void save(value);
    } else {
      cancel();
    }
  };

  return (
    <div className={styles.editable}>
      {editable ? (
        <TextInput
          size="sm"
          autoFocus
          aria-label="Device type name"
          defaultValue={currentValue}
          className={styles.nameInput}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              commit(event.currentTarget.value);
            } else if (event.key === "Escape") {
              cancel();
            }
          }}
        />
      ) : (
        <span
          className={classNames(styles.title, styles.nameDisplay)}
          role="button"
          tabIndex={0}
          onClick={beginEdit}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              beginEdit();
            }
          }}
        >
          {currentValue}
        </span>
      )}
      {error && (
        <Callout variant="error">
          {userFacingErrorMessage(error, "Failed to update device type name.")}
        </Callout>
      )}
    </div>
  );
}
